import React, { useState, useEffect, useCallback } from 'react';
import { Camera, Globe, Mail, Bell, Shield, CreditCard, ChevronRight, Loader2, Save, ToggleLeft, ToggleRight, Zap, BarChart3, ArrowLeft, Check, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { apiUrl } from '../../utils/api';

type BillingView = 'none' | 'manage' | 'upgrade' | 'checkout';

const SettingsPage: React.FC = () => {
  const { user, token } = useAuth() as any;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organization, setOrganization] = useState<any>(null);
  const [billingView, setBillingView] = useState<BillingView>('none');

  // Editable fields
  const [orgName, setOrgName] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canEdit = user?.role === 'Org Admin' || user?.role === 'Super Admin';
  const userDomain = user?.email ? user.email.split('@')[1] : 'engazeiq.ai';
  const defaultSupportEmail = userDomain;

  // Feature flags state
  const [summarizationEnabled, setSummarizationEnabled] = useState(true);
  const [evaluationEnabled, setEvaluationEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  const [featuresSaving, setFeaturesSaving] = useState(false);
  const [featuresLastUpdated, setFeaturesLastUpdated] = useState<string | null>(null);

  // Usage / execution tracking state
  const [usageUsed, setUsageUsed] = useState(0);
  const [usageLimit, setUsageLimit] = useState(50);
  const [usagePercent, setUsagePercent] = useState(0);

  const fetchUsage = useCallback(async () => {
    try {
      const plan = organization?.plan || 'Free';
      const params = new URLSearchParams({ plan });
      if (user?.organization_id) {
        params.set('org_id', user.organization_id);
      }
      const response = await fetch(apiUrl(`/api/usage?${params.toString()}`));
      if (response.ok) {
        const data = await response.json();
        setUsageUsed(data.used);
        setUsageLimit(data.limit);
        setUsagePercent(data.percent);
      }
    } catch (err) {
      console.error('Error fetching usage:', err);
    }
  }, [organization?.plan, user?.organization_id]);

  const fetchFeatureFlags = useCallback(async () => {
    try {
      const params = user?.organization_id ? `?org_id=${user.organization_id}` : '';
      const response = await fetch(apiUrl(`/api/features${params}`));
      if (response.ok) {
        const data = await response.json();
        setSummarizationEnabled(data.summarization_enabled);
        setEvaluationEnabled(data.evaluation_enabled);
        setNotificationsEnabled(data.notifications_enabled ?? true);
        setFeaturesLastUpdated(data.last_updated || null);
      }
    } catch (err) {
      console.error('Error fetching feature flags:', err);
    } finally {
      setFeaturesLoading(false);
    }
  }, [user?.organization_id]);

  const toggleFeature = async (feature: 'summarization_enabled' | 'evaluation_enabled' | 'notifications_enabled', value: boolean) => {
    setFeaturesSaving(true);
    try {
      const params = user?.organization_id ? `?org_id=${user.organization_id}` : '';
      const response = await fetch(apiUrl(`/api/features${params}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [feature]: value }),
      });
      if (response.ok) {
        const data = await response.json();
        setSummarizationEnabled(data.summarization_enabled);
        setEvaluationEnabled(data.evaluation_enabled);
        setNotificationsEnabled(data.notifications_enabled ?? true);
        setFeaturesLastUpdated(data.last_updated || null);
      }
    } catch (err) {
      console.error('Error updating feature flag:', err);
    } finally {
      setFeaturesSaving(false);
    }
  };

  useEffect(() => {
    const fetchOrg = async () => {
      if (!user?.organization_id || !token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(apiUrl(`/api/auth/organizations/${user.organization_id}`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setOrganization(data);
          setOrgName(data.name);
          setSupportEmail(data.support_email || defaultSupportEmail);
        } else {
          setError('Failed to load organization settings');
        }
      } catch (err) {
        console.error('Error fetching org:', err);
        setError('Network error loading settings');
      } finally {
        setLoading(false);
      }
    };

    fetchOrg();
  }, [user?.organization_id, token]);

  useEffect(() => {
    fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // Re-fetch usage when billing view is opened
  useEffect(() => {
    if (billingView === 'manage') {
      fetchUsage();
    }
  }, [billingView, fetchUsage]);

  const handleSaveSettings = async () => {
    if (!token || !user?.organization_id) return;

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(apiUrl(`/api/auth/organizations/${user.organization_id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: orgName,
          support_email: supportEmail
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setOrganization(updated);
        // Maybe show a success toast here if available in context
        alert('Settings saved successfully!');
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Network error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (organization) {
      setOrgName(organization.name);
      setSupportEmail(organization.support_email || defaultSupportEmail);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-12 pb-12 transition-colors">

      {billingView === 'none' && (<>
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Account Preferences</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Configure global platform behavior and account preferences.</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-sm">
          {error}
        </div>
      )}

      <section className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden transition-colors">
        <div className="p-6 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50">
          <h3 className="font-bold text-slate-900 dark:text-white">Organization Profile</h3>
        </div>
        <div className="p-8 space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-8">
            <div className="relative">
              <div className="w-24 h-24 bg-slate-100 dark:bg-slate-700 rounded-3xl flex items-center justify-center text-slate-400 dark:text-slate-500 overflow-hidden border border-slate-200 dark:border-slate-600 transition-colors">
                <img src={organization?.logo_url || "https://picsum.photos/seed/org/200"} alt="Org Logo" />
              </div>
              <button className="absolute -bottom-2 -right-2 p-2 bg-white dark:bg-slate-700 rounded-xl border border-slate-200 dark:border-slate-600 shadow-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                <Camera size={16} />
              </button>
            </div>
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-tight">Organization Name</label>
                  <input
                    type="text"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 dark:text-white rounded-xl text-sm focus:outline-none focus:border-blue-500 disabled:opacity-60 disabled:bg-slate-100 dark:disabled:bg-slate-800 transition-colors"
                    disabled={!canEdit}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-tight">Support Email</label>
                  <input
                    type="email"
                    value={supportEmail}
                    onChange={(e) => setSupportEmail(e.target.value)}
                    className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 dark:text-white rounded-xl text-sm focus:outline-none focus:border-blue-500 disabled:opacity-60 disabled:bg-slate-100 dark:disabled:bg-slate-800 transition-colors"
                    disabled={!canEdit}
                  />
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Features and Options */}
      <section className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden transition-colors">
        <div className="p-6 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50">
          <h3 className="font-bold text-slate-900 dark:text-white">Features and Options</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Enable or disable expensive AI features to manage token usage dynamically.</p>
        </div>
        <div className="p-8 space-y-6">
          {featuresLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-blue-600" size={24} />
            </div>
          ) : (
            <>
              {/* Summarization Toggle */}
              <div className="flex items-center justify-between p-5 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-700 hover:border-blue-200 dark:hover:border-blue-800 transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/40 rounded-xl flex items-center justify-center">
                    <BarChart3 className="text-blue-600 dark:text-blue-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white text-sm">Summarization</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Fetches past 7-day conversations from Memory Bank and generates weekly summaries.
                    </p>
                    {featuresLastUpdated && summarizationEnabled && (
                      <p className="text-[10px] text-blue-500 mt-1 font-medium">
                        Last updated: {new Date(featuresLastUpdated).toLocaleString()}
                      </p>
                    )}
                    {!summarizationEnabled && (
                      <p className="text-[10px] text-amber-500 mt-1 font-medium">
                        Paused — no new summaries or memory fetches will occur
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => toggleFeature('summarization_enabled', !summarizationEnabled)}
                  disabled={featuresSaving || !canEdit}
                  className="flex-shrink-0 disabled:opacity-50 transition-all"
                  title={summarizationEnabled ? 'Disable Summarization' : 'Enable Summarization'}
                >
                  {summarizationEnabled ? (
                    <ToggleRight className="text-blue-600" size={36} />
                  ) : (
                    <ToggleLeft className="text-slate-400" size={36} />
                  )}
                </button>
              </div>

              {/* Evaluation Toggle */}
              <div className="flex items-center justify-between p-5 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-700 hover:border-indigo-200 dark:hover:border-indigo-800 transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/40 rounded-xl flex items-center justify-center">
                    <Zap className="text-indigo-600 dark:text-indigo-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white text-sm">Evaluation</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Runs realtime evaluation during chat to monitor agent quality and performance.
                    </p>
                    {!evaluationEnabled && (
                      <p className="text-[10px] text-amber-500 mt-1 font-medium">
                        Paused — evaluation metrics will not update until re-enabled
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => toggleFeature('evaluation_enabled', !evaluationEnabled)}
                  disabled={featuresSaving || !canEdit}
                  className="flex-shrink-0 disabled:opacity-50 transition-all"
                  title={evaluationEnabled ? 'Disable Evaluation' : 'Enable Evaluation'}
                >
                  {evaluationEnabled ? (
                    <ToggleRight className="text-indigo-600" size={36} />
                  ) : (
                    <ToggleLeft className="text-slate-400" size={36} />
                  )}
                </button>
              </div>

              {/* Notifications Toggle */}
              <div className="flex items-center justify-between p-5 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-700 hover:border-emerald-200 dark:hover:border-emerald-800 transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/40 rounded-xl flex items-center justify-center">
                    <Bell className="text-emerald-600 dark:text-emerald-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white text-sm">Notifications</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Sends admin email alerts for signups, agent failures, and weekly usage digests.
                    </p>
                    {!notificationsEnabled && (
                      <p className="text-[10px] text-amber-500 mt-1 font-medium">
                        Paused — no notification emails will be sent until re-enabled
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => toggleFeature('notifications_enabled', !notificationsEnabled)}
                  disabled={featuresSaving || !canEdit}
                  className="flex-shrink-0 disabled:opacity-50 transition-all"
                  title={notificationsEnabled ? 'Disable Notifications' : 'Enable Notifications'}
                >
                  {notificationsEnabled ? (
                    <ToggleRight className="text-emerald-600" size={36} />
                  ) : (
                    <ToggleLeft className="text-slate-400" size={36} />
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Subscription Card */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 p-8 rounded-3xl text-white shadow-xl shadow-blue-500/20">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Shield size={20} className="text-blue-200" />
              <span className="text-xs font-bold uppercase tracking-widest text-blue-100">Plan Summary</span>
            </div>
            <h2 className="text-3xl font-bold">{organization?.plan || 'Enterprise Tier'}</h2>

          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setBillingView('manage')}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-2xl text-sm font-bold backdrop-blur-md transition-all"
            >
              Manage Billing
            </button>
            <button
              onClick={() => setBillingView('upgrade')}
              className="px-6 py-3 bg-white text-blue-600 rounded-2xl text-sm font-bold shadow-lg shadow-black/10 hover:scale-105 transition-all"
            >
              Upgrade
            </button>
          </div>
        </div>
      </section>

      {canEdit && (
        <div className="flex items-center justify-end gap-3 pt-6">
          <button
            onClick={handleDiscard}
            className="px-6 py-3 text-sm font-bold text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
          >
            Discard Changes
          </button>
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-2xl text-sm font-bold shadow-lg shadow-blue-500/20 hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}
      </>)}

      {/* ==================== BILLING INLINE VIEWS ==================== */}

      {/* Manage Billing – Full Page */}
      {billingView === 'manage' && (
        <div className="space-y-10">
            {/* Back nav */}
            <button
              onClick={() => setBillingView('none')}
              className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Account Preferences
            </button>

            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Billing Settings</h1>

            {/* Current Plan Card */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 space-y-6">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">Current Plan</p>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{organization?.plan || 'Free'}</h2>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Usage This Month</p>
                  <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">{usageUsed} / {usageLimit} included executions</span>
                </div>
                <div className="h-2.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ease-out ${
                      usagePercent >= 100
                        ? 'bg-gradient-to-r from-red-400 to-red-500'
                        : usagePercent >= 80
                          ? 'bg-gradient-to-r from-amber-400 to-amber-500'
                          : 'bg-gradient-to-r from-blue-400 to-blue-500'
                    }`}
                    style={{ width: `${Math.min(usagePercent, 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-slate-400">{usagePercent}% used</p>
              </div>
            </div>

            {/* Choose Your Plan */}
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Choose Your Plan</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Free Plan */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/10 rounded-2xl p-7 flex flex-col justify-between space-y-5 transition-all">
                  <div className="space-y-5">
                    <div>
                      <h3 className="text-base font-bold text-slate-900 dark:text-white">Free Plan</h3>
                      <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">Free</p>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                      Build an agentic workflow today, and see what's possible with collaborative AI agents.
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-start gap-2.5">
                        <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">Visual editor and AI copilot</span>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">GitHub integration</span>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">50 workflow executions/month</span>
                      </div>
                    </div>
                  </div>
                  <button
                    disabled
                    className="w-full py-3 bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-semibold cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                </div>

                {/* Professional Plan */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/10 rounded-2xl p-7 flex flex-col justify-between space-y-5 relative transition-all">
                  <div className="space-y-5">
                    <div>
                      <h3 className="text-base font-bold text-slate-900 dark:text-white">Professional Plan</h3>
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-2xl font-bold text-slate-900 dark:text-white">USD 25.00</span>
                        <span className="text-sm text-slate-500 dark:text-slate-400">/month</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                      Deploy production AI agents that automate complex tasks as often as needed.
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-start gap-2.5">
                        <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">Github Integrations and deployment</span>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">100 included workflow executions/month</span>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">$0.50 per additional workflow execution</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setBillingView('checkout')}
                    className="w-full py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-100 transition-all"
                  >
                    Choose Plan
                  </button>
                </div>

                {/* Enterprise */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/10 rounded-2xl p-7 flex flex-col justify-between space-y-5 transition-all">
                  <div className="space-y-5">
                    <div>
                      <h3 className="text-base font-bold text-slate-900 dark:text-white">Enterprise</h3>
                      <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">Contact Sales</p>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                      Accelerate and scale Agentic AI adoption across the organization.
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-start gap-2.5">
                        <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">Engazeiq or private infrastructure</span>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">On-site support and training</span>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">50 hours of development/month</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => window.open('mailto:sales@engazeiq.ai?subject=Enterprise Plan Inquiry', '_blank')}
                    className="w-full py-3 bg-white dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
                  >
                    Contact Sales
                  </button>
                </div>
              </div>

              {/* Footer */}
              <p className="text-center text-sm text-slate-500 dark:text-slate-400 pt-4">
                Have questions about our plans? Contact our sales team: <a href="mailto:sales@engazeiq.ai" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">sales@engazeiq.ai</a>
              </p>
            </div>
        </div>
      )}

      {/* Upgrade – Professional + Enterprise Only */}
      {billingView === 'upgrade' && (
        <div className="space-y-10">
            {/* Back nav */}
            <button
              onClick={() => setBillingView('none')}
              className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Account Preferences
            </button>

            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Upgrade Your Plan</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">Choose the plan that best fits your needs.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Professional Plan */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/10 rounded-2xl p-8 flex flex-col justify-between space-y-6 relative transition-all">
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 bg-blue-600 text-white text-[10px] font-bold uppercase tracking-widest rounded-full shadow-md">Recommended</span>
                </div>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">Professional Plan</h3>
                    <div className="flex items-baseline gap-1 mt-2">
                      <span className="text-3xl font-bold text-slate-900 dark:text-white">USD 25.00</span>
                      <span className="text-sm text-slate-500 dark:text-slate-400">/month</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                    Deploy production AI agents that automate complex tasks as often as needed.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2.5">
                      <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">Github Integrations and deployment</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">100 included workflow executions/month</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">$0.50 per additional workflow execution</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setBillingView('checkout')}
                  className="w-full py-3.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-100 transition-all"
                >
                  Choose Plan
                </button>
              </div>

              {/* Enterprise */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/10 rounded-2xl p-8 flex flex-col justify-between space-y-6 transition-all">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">Enterprise</h3>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-slate-900 dark:text-white">Contact Sales</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                    Accelerate and scale Agentic AI adoption across the organization.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2.5">
                      <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">CrewAI or private infrastructure</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">On-site support and training</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <Check size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">50 hours of development/month</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => window.open('mailto:sales@engazeiq.ai?subject=Enterprise Plan Inquiry', '_blank')}
                  className="w-full py-3.5 bg-white dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
                >
                  Contact Sales
                </button>
              </div>
            </div>

            <p className="text-center text-sm text-slate-500 dark:text-slate-400 pt-2">
              Have questions about our plans? Contact our sales team: <a href="mailto:sales@engazeiq.ai" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">sales@engazeiq.ai</a>
            </p>
        </div>
      )}

      {/* Payment / Checkout */}
      {billingView === 'checkout' && (
        <div className="space-y-8">
            {/* Back nav */}
            <button
              onClick={() => setBillingView('upgrade')}
              className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Plans
            </button>

            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Subscribe to Professional Plan</h1>

            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2">
                {/* Left – Order Summary */}
                <div className="p-8 space-y-6 border-r border-slate-200 dark:border-slate-700">
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500 dark:text-slate-400">Subscribe to Professional Plan and 1 more</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">$25.00 <span className="text-sm font-normal text-slate-500">due today</span></p>
                    <p className="text-xs text-slate-400">Then starting at $25.00 per month</p>
                  </div>

                <div className="space-y-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">Professional Plan</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Deploy production AI agents that automate complex tasks as often as needed.</p>
                      <p className="text-[10px] text-slate-400 mt-1">Qty 1. Billed monthly</p>
                    </div>
                    <span className="text-sm font-semibold text-slate-900 dark:text-white">$25.00</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">Professional Usage Based</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Includes 100 executions. $0.50 per additional execution.</p>
                      <p className="text-[10px] text-slate-400 mt-1">Billed monthly based on usage</p>
                    </div>
                    <span className="text-sm text-slate-500 dark:text-slate-400">Price varies</span>
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Subtotal</span>
                    <span className="font-semibold text-slate-900 dark:text-white">$25.00</span>
                  </div>
                  <button className="text-xs text-blue-600 dark:text-blue-400 font-semibold hover:underline">
                    Add promotion code
                  </button>
                  <div className="flex items-center justify-between text-sm pt-3 border-t border-slate-200 dark:border-slate-700">
                    <span className="font-bold text-slate-900 dark:text-white">Total due today</span>
                    <span className="font-bold text-slate-900 dark:text-white">$25.00</span>
                  </div>
                </div>
              </div>

              {/* Right – Payment Form */}
              <div className="p-8 space-y-6">
                {/* Pay with Link button */}
                <button className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2">
                  Pay with <span className="font-extrabold tracking-tight">&#9889; link</span>
                </button>

                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700"></div>
                  <span className="text-xs text-slate-400 uppercase font-medium">or</span>
                  <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700"></div>
                </div>

                {/* Contact Information */}
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">Contact information</p>
                  <div className="space-y-1.5">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">Email</label>
                    <input
                      type="email"
                      defaultValue={user?.email || ''}
                      readOnly
                      className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Payment Method */}
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">Payment method</p>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-xl cursor-pointer hover:border-blue-300 dark:hover:border-blue-600 transition-colors">
                      <input type="radio" name="payment" defaultChecked className="accent-blue-600" />
                      <CreditCard size={18} className="text-slate-500" />
                      <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">Card</span>
                      <div className="ml-auto flex items-center gap-1">
                        <div className="w-8 h-5 bg-blue-700 rounded text-[8px] text-white font-bold flex items-center justify-center">VISA</div>
                        <div className="w-8 h-5 bg-red-500 rounded text-[8px] text-white font-bold flex items-center justify-center">MC</div>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-xl cursor-pointer hover:border-blue-300 dark:hover:border-blue-600 transition-colors">
                      <input type="radio" name="payment" className="accent-blue-600" />
                      <Shield size={18} className="text-slate-500" />
                      <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">US bank account</span>
                    </label>
                  </div>
                </div>

                {/* Card Details (placeholder) */}
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">Card number</label>
                    <input
                      type="text"
                      placeholder="1234 1234 1234 1234"
                      className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">Expiry</label>
                      <input
                        type="text"
                        placeholder="MM / YY"
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">CVC</label>
                      <input
                        type="text"
                        placeholder="CVC"
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Subscribe Button */}
                <button className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-blue-500/20">
                  Subscribe
                </button>

                <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                  By subscribing, you authorize EngazeIQ to charge you according to these terms until you cancel.
                </p>
              </div>
              </div>
            </div>
        </div>
      )}

    </div>
  );
};

export default SettingsPage;
